import { create } from 'zustand';

import type { FolderConflictChoice } from '../lib/folderUpload';

/** Top-level folders of a folder upload whose names are taken in the target folder. */
export interface FolderConflictRequest {
  /** The names, as they exist in the target folder. */
  names: string[];
  /** Folder the upload goes to (shown in the text). */
  folderName: string;
  /** At least one of the names is a folder, so the new one can be merged into it. */
  allowMerge: boolean;
}

interface Pending extends FolderConflictRequest {
  id: number;
  resolve: (choice: FolderConflictChoice | null) => void;
}

interface FolderConflictState {
  current: Pending | null;
  queue: Pending[];
  push: (item: Pending) => void;
  settle: (id: number, choice: FolderConflictChoice | null) => void;
}

let nextId = 1;

export const useFolderConflictStore = create<FolderConflictState>()((set, get) => ({
  current: null,
  queue: [],
  push: (item) => {
    if (get().current) set((s) => ({ queue: [...s.queue, item] }));
    else set({ current: item });
  },
  settle: (id, choice) => {
    const { current, queue } = get();
    if (current?.id !== id) return;
    current.resolve(choice);
    const [next = null, ...rest] = queue;
    set({ current: next, queue: rest });
  },
}));

/**
 * Asks what to do with folders of an upload that already exist: keep both, merge (replacing or keeping the files that
 * are there) or skip. Resolves with the choice, `null` when cancelled. Needs `<ConflictDialogHost />`.
 */
export function askFolderConflict(request: FolderConflictRequest): Promise<FolderConflictChoice | null> {
  return new Promise((resolve) => {
    useFolderConflictStore.getState().push({ ...request, id: nextId++, resolve });
  });
}
